import type { Booking, FlightSegment, BookingStats, PriceCheck, UserProfile, TripStatus } from '../lib/types';

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

/** Returns an ISO date string `days` days in the future. */
export function daysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

/** Returns an ISO date string `days` days in the past. */
export function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

// ---------------------------------------------------------------------------
// Unique ID generator
// ---------------------------------------------------------------------------

let _counter = 0;
function uid(prefix = 'id'): string {
  return `${prefix}-${++_counter}-${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// Segment factory
// ---------------------------------------------------------------------------

/** Creates a FlightSegment with sensible defaults (JFK->LAX, DL100). */
export function createSegment(overrides?: Partial<FlightSegment>): FlightSegment {
  const departureBase = daysFromNow(15);
  const arrivalBase = new Date(new Date(departureBase).getTime() + 5.5 * 60 * 60 * 1000).toISOString();
  const bookingId = overrides?.booking_id ?? uid('booking');

  return {
    id: uid('seg'),
    booking_id: bookingId,
    segment_number: 1,
    carrier_code: 'DL',
    flight_number: 'DL100',
    origin: 'JFK',
    destination: 'LAX',
    departure_datetime: departureBase,
    arrival_datetime: arrivalBase,
    cabin_code: 'Y',
    booking_class: 'L',
    ...overrides,
  } satisfies FlightSegment;
}

// ---------------------------------------------------------------------------
// Booking factory
// ---------------------------------------------------------------------------

/**
 * Creates a Booking pre-configured for a given TripStatus state.
 * Use 'default' for a generic active booking with no special status.
 * Overrides are shallow-merged at the top level (segments array is replaceable).
 */
export function createBooking(
  state: TripStatus | 'default' = 'default',
  overrides?: Partial<Booking>,
): Booking {
  const id = overrides?.id ?? uid('booking');

  const defaultSegment = createSegment({ booking_id: id });

  const base: Booking = {
    id,
    user_id: uid('user'),
    pnr: uid('PNR'),
    airline_code: 'DL',
    booking_date: new Date().toISOString().slice(0, 10),
    total_price_paid: 300,
    currency: 'USD',
    is_award_booking: false,
    cabin_class: 'economy',
    is_active: true,
    booking_status: 'watching',
    is_changeable: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    segments: [defaultSegment],
    has_cheaper_option: false,
    potential_savings: 0,
  };

  // Apply state-specific defaults
  let stateDefaults: Partial<Booking> = {};

  switch (state) {
    case 'watching':
      stateDefaults = {
        is_active: true,
        has_cheaper_option: false,
        booking_status: 'watching',
        segments: [createSegment({ booking_id: id })],
      };
      break;

    case 'drop_found':
      stateDefaults = {
        is_active: true,
        has_cheaper_option: true,
        potential_savings: 50,
        latest_price: 250,
        booking_status: 'price_drop',
        segments: [createSegment({ booking_id: id })],
      };
      break;

    case 'rebooked':
      stateDefaults = {
        is_active: true,
        booking_status: 'rebooked',
        potential_savings: 75,
        has_cheaper_option: false,
        segments: [createSegment({ booking_id: id })],
      };
      break;

    case 'departed':
      stateDefaults = {
        is_active: true,
        booking_status: 'watching',
        segments: [
          createSegment({
            booking_id: id,
            departure_datetime: daysAgo(3),
            arrival_datetime: daysAgo(2),
          }),
        ],
      };
      break;

    case 'stopped':
      stateDefaults = {
        is_active: false,
        booking_status: 'stopped',
        segments: [createSegment({ booking_id: id })],
      };
      break;

    case 'saved':
      stateDefaults = {
        is_active: true,
        airline_code: 'B6',
        booking_status: 'watching',
        segments: [createSegment({ booking_id: id, carrier_code: 'B6', flight_number: 'B6200' })],
      };
      break;

    case 'default':
    default:
      // Use base as-is
      break;
  }

  return {
    ...base,
    ...stateDefaults,
    ...overrides,
  } satisfies Booking;
}

// ---------------------------------------------------------------------------
// Stats factory
// ---------------------------------------------------------------------------

/** Creates a BookingStats object with zeroed defaults. */
export function createStats(overrides?: Partial<BookingStats>): BookingStats {
  return {
    total_bookings: 0,
    active_bookings: 0,
    bookings_with_cheaper_options: 0,
    total_potential_savings: 0,
    total_cash_savings: 0,
    total_points_savings: 0,
    cash_bookings: 0,
    award_bookings: 0,
    days_monitoring: 0,
    ...overrides,
  } satisfies BookingStats;
}

// ---------------------------------------------------------------------------
// Price history factory
// ---------------------------------------------------------------------------

/** Creates an array of PriceCheck records for a booking. */
export function createPriceHistory(bookingId: string, count = 5): PriceCheck[] {
  const basePrice = 300;
  return Array.from({ length: count }, (_, i) => {
    const checkedAt = new Date();
    checkedAt.setDate(checkedAt.getDate() - (count - i));
    const price = basePrice - i * 5; // gradual decrease

    return {
      id: uid('pc'),
      booking_id: bookingId,
      checked_at: checkedAt.toISOString(),
      data_source: 'google-flights',
      current_price: price,
      currency: 'USD',
      is_available: true,
      is_cheaper: price < basePrice,
      price_difference: price < basePrice ? basePrice - price : undefined,
      price_difference_percent: price < basePrice ? Math.round(((basePrice - price) / basePrice) * 100) : undefined,
    } satisfies PriceCheck;
  });
}

// ---------------------------------------------------------------------------
// Profile factory
// ---------------------------------------------------------------------------

/** Creates a UserProfile with sensible defaults. */
export function createProfile(overrides?: Partial<UserProfile>): UserProfile {
  return {
    id: uid('user'),
    first_name: 'Test',
    last_name: 'User',
    email_notifications_enabled: true,
    telegram_chat_id: null,
    telegram_username: null,
    telegram_linked_at: null,
    deletion_requested_at: null,
    onboarding_completed_at: new Date().toISOString(),
    stripe_customer_id: null,
    card_brand: null,
    card_last4: null,
    card_exp_month: null,
    card_exp_year: null,
    referral_code: null,
    commission_waived_next: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    ...overrides,
  } satisfies UserProfile;
}
